import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import type {
    ClosingOverview,
    OverviewAccountRef,
    OverviewMissingProof,
    OverviewPosition,
    OverviewStepState,
} from '../../presenters/closing-overview.presenter';

/** Where a button on the overview leads. The container knows the routes. */
export type OverviewTarget = 'chartOfAccounts' | 'documents' | 'balanceSheet' | 'incomeStatement';

/** The circle of a step: the three states of a real step, and `soon` for one that does not exist yet. */
export type OverviewCircle = OverviewStepState | 'soon';

const AMOUNT = new Intl.NumberFormat('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * REV-22476 (Part 3) — the Jahresabschluss overview (pure view): what is to do in this closing, step
 * by step, modelled on the AWF prototype.
 *
 * Six steps on a timeline. Kontenplan, Dokumente and Bilanzkonten carry real data (`ClosingOverview`);
 * Erfolgsrechnung only leads to its page; Mandanten-Kontext, Eröffnungsanalyse and Abschluss are
 * "Demnächst" — shown, never clickable.
 *
 * `locked` = the Kontenplan is not complete: everything behind the closing workspace's gate is shown
 * but its buttons are disabled, with the reason as tooltip.
 *
 * `waiting` = the accounting data of a fresh closing is still being imported (REV-22527): every step
 * reads the accounts, so all of them wait, the Kontenplan included.
 */
@Component({
    selector: 'abcp-closing-overview',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './closing-overview.component.html',
    styleUrls: ['./closing-overview.component.scss'],
})
export class AbcpClosingOverviewComponent {
    @Input() year: string | null = null;
    @Input() overview: ClosingOverview | null = null;
    @Input() loading = false;
    @Input() locked = false;
    /** The accounting data is still being imported. Locks every step until it is in. */
    @Input() waiting = false;

    @Output() open = new EventEmitter<OverviewTarget>();
    @Output() openPosition = new EventEmitter<string>();
    /** The closing settings (business year, currency) — opened from the overview, not the navigation. */
    @Output() settings = new EventEmitter<void>();

    /** The four checks of the Eröffnungsanalyse — named so the user sees what is coming. */
    public readonly openingChecks = ['openingBalance', 'vatRates', 'bookingPlausibility', 'profitAllocation'];

    /** Whether the steps behind the workspace's gate can be opened right now. */
    get blocked(): boolean {
        return this.waiting || this.locked;
    }

    /** Why they cannot: the import comes first, the incomplete Kontenplan second. */
    get lockReasonKey(): string {
        return this.waiting ? 'abcp.setup.import.locked' : 'abcp.chartOfAccounts.locked';
    }

    public circleOf(step: 1 | 3 | 4): OverviewCircle {
        if (this.overview === null) {
            return 'none';
        }
        switch (step) {
            case 1:
                return this.overview.chartOfAccounts.state;
            case 3:
                return this.overview.documents.state;
            case 4:
                return this.overview.balanceSheet.state;
        }
    }

    public accountsLine(accounts: readonly OverviewAccountRef[]): string {
        return accounts.map((account) => `${account.accountNumber} ${account.name}`).join(' · ');
    }

    /** «Kontoauszug, Zins- / Kapitalausweis oder Bankbestätigung» — every document that would do. */
    public kindsText(labels: readonly string[], or: string): string {
        return labels.length <= 1
            ? labels[0] ?? ''
            : `${labels.slice(0, -1).join(', ')} ${or} ${labels[labels.length - 1]}`;
    }

    public amount(value: number): string {
        return AMOUNT.format(value);
    }

    public trackByFunction(_: number, row: OverviewMissingProof): string {
        return row.accountFunction;
    }

    public trackByPosition(_: number, row: OverviewPosition): string {
        return row.position;
    }

    public onPosition(position: string): void {
        if (!this.blocked) {
            this.openPosition.emit(position);
        }
    }
}
